import React from "react";
import { Shield, Heart, AlertCircle, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";

const SafetyNetSection = () => {
  const insuranceTypes = [
    {
      id: "life",
      icon: Shield,
      title: "Life Insurance",
      subtitle: "Protect Your Family's Future",
      description:
        "If something happens to you, your family's dreams shouldn't stop. Term insurance ensures they continue living the life you planned for them.",
      coverageText: "20x Annual Income Coverage",
      color: "emerald",
      features: [
        "Tax benefits under Section 80C",
        "Affordable premiums",
        "Covers loans & liabilities",
        "Child's education secured",
      ],
    },
    {
      id: "health",
      icon: Heart,
      title: "Health Insurance",
      subtitle: "Preserve Your Wealth",
      description:
        "A single hospitalization can wipe out years of investments. Health insurance ensures medical emergencies don't become financial emergencies.",
      coverageText: "₹10-50 Lakh Coverage",
      color: "rose",
      features: [
        "Cashless hospitalization",
        "Pre & post hospitalization",
        "No-claim bonus",
        "Critical illness cover",
      ],
    },
  ];

  return (
    <section
      id="safety-net"
      className="py-24 sm:py-32 bg-white"
      data-testid="safety-net-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-emerald-600 font-semibold mb-4">
            <Shield className="w-4 h-4" />
            The Safety Net
          </span>
          <h2
            className="text-3xl sm:text-4xl tracking-tight font-semibold leading-snug text-emerald-950 mb-4"
            data-testid="safety-net-title"
          >
            Your Wealth Plan is Incomplete Without Protection
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Don't let a hospital bill or an untimely event wipe out your Mutual Fund
            corpus.{" "}
            <strong className="text-emerald-700">We protect what you build.</strong>
          </p>
        </div>

        {/* Warning Banner */}
        <div
          className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-12 flex flex-col md:flex-row items-start md:items-center gap-4"
          data-testid="warning-banner"
        >
          <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h4 className="font-semibold text-amber-900 mb-1">Did You Know?</h4>
            <p className="text-amber-800">
              <strong>46% of Indian families</strong> had to dip into their savings or
              sell assets to pay for medical emergencies. And{" "}
              <strong>₹4.6 Lakh</strong> is the average cost of cancer treatment – enough
              to wipe out 5 years of SIP investments.
            </p>
          </div>
        </div>

        {/* Insurance Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {insuranceTypes.map((insurance) => (
            <div
              key={insurance.id}
              className="relative group"
              data-testid={`insurance-card-${insurance.id}`}
            >
              <div
                className={`absolute inset-0 rounded-3xl ${
                  insurance.color === "emerald"
                    ? "bg-emerald-500/10"
                    : "bg-rose-500/10"
                } opacity-0 group-hover:opacity-100 transition-opacity duration-300`}
              />
              <div className="relative glass-card rounded-3xl p-8 shadow-xl shadow-slate-900/5 hover-lift h-full">
                <div className="flex items-start gap-4 mb-6">
                  <div
                    className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                      insurance.color === "emerald"
                        ? "bg-emerald-100 text-emerald-600"
                        : "bg-rose-100 text-rose-600"
                    }`}
                  >
                    <insurance.icon className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-emerald-950">
                      {insurance.title}
                    </h3>
                    <p className="text-sm text-slate-500">{insurance.subtitle}</p>
                  </div>
                </div>

                <p className="text-slate-600 mb-6 leading-relaxed">
                  {insurance.description}
                </p>

                <div
                  className={`inline-block px-4 py-2 rounded-full text-sm font-semibold mb-6 ${
                    insurance.color === "emerald"
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-rose-100 text-rose-700"
                  }`}
                >
                  {insurance.coverageText}
                </div>

                <div className="space-y-3">
                  {insurance.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <CheckCircle2
                        className={`w-5 h-5 ${
                          insurance.color === "emerald"
                            ? "text-emerald-500"
                            : "text-rose-500"
                        }`}
                      />
                      <span className="text-sm text-slate-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Image Section */}
        <div className="relative rounded-3xl overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1772683432682-3bd5ecebc980?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxoYXBweSUyMGluZGlhbiUyMGZhbWlseSUyMGxpZmVzdHlsZSUyMG91dGRvb3JzfGVufDB8fHx8MTc3NTQ1NzU4OXww&ixlib=rb-4.1.0&q=85&w=1200"
            alt="Happy family protected with insurance"
            className="w-full h-[300px] md:h-[400px] object-cover"
            data-testid="family-image"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-950/80 via-emerald-950/60 to-transparent flex items-center">
            <div className="p-8 md:p-12 max-w-xl">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Build Wealth. Protect Dreams.
              </h3>
              <p className="text-emerald-100 mb-6">
                A comprehensive financial plan includes both wealth creation and wealth
                protection. Let us help you build both.
              </p>
              <Button
                asChild
                className="bg-white text-emerald-900 hover:bg-emerald-50 rounded-full px-8 py-6 text-lg shadow-lg"
                data-testid="insurance-cta-button"
              >
                <a href="#contact">Get Free Insurance Quote</a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SafetyNetSection;
